// The console (WI11): sign in, then the ops home for the WhatsApp flow: the approval queue with its
// count, the cases and their conversations, the emergency banner, and the flow's settings for tenant
// admins; and (WI12) members, tasks, the members' dashboard, payments and the Internal AIs tracker; and
// (WI15) the 30-day trial's leads.
// The SOP editor, the tenant admin screen and the agent builder (step 4) grow here.
import { useEffect, useState } from "react";

import { api, refresh, signIn, signOut, type Me } from "./api";
import { Household, Members } from "./Members";
import { Case, Cases, Queue, Settings, usePoll } from "./Pages";
import { Payments } from "./Payments";
import { Dashboard, TaskPage, Tasks, Tracker } from "./Tasks";
import { TrialPage, Trials } from "./Trials";
import type { Inbox } from "./types";

const ROLE_NAMES: Record<string, string> = {
  platform_operator: "Platform operator",
  tenant_admin: "Tenant admin",
  sop_editor: "SOP editor",
  publisher: "Publisher",
  ops_agent: "Ops agent",
  end_user: "Member",
};
const OPS = ["ops_agent", "tenant_admin", "platform_operator"];
const ADMINS = ["tenant_admin", "platform_operator"];

type State = { kind: "loading" } | { kind: "signed-out" } | { kind: "signed-in"; me: Me } | { kind: "error"; message: string };

function useHash(): [string, (to: string) => void] {
  const [hash, setHash] = useState(window.location.hash.slice(1) || "/");
  useEffect(() => {
    const changed = () => setHash(window.location.hash.slice(1) || "/");
    window.addEventListener("hashchange", changed);
    return () => window.removeEventListener("hashchange", changed);
  }, []);
  return [hash, (to: string) => (window.location.hash = to)];
}

function Console({ me, onSignOut }: { me: Me; onSignOut: () => void }) {
  const [route, go] = useHash();
  const { data: inbox } = usePoll(() => api<Inbox>("/api/inbox"), 10000);
  const admin = me.roles.some((r) => ADMINS.includes(r));
  const waiting = inbox ? inbox.approvals : 0;

  useEffect(() => {
    document.title = waiting > 0 ? `(${waiting}) Console` : "Console";
  }, [waiting]);

  const open = (id: string) => go(`/cases/${id}`);
  const member = (id: string) => go(`/members/${id}`);
  const task = (id: string) => go(`/tasks/${id}`);
  const caseId = route.startsWith("/cases/") ? route.slice("/cases/".length) : null;
  const memberId = route.startsWith("/members/") ? route.slice("/members/".length) : null;
  const taskId = route.startsWith("/tasks/") ? route.slice("/tasks/".length) : null;
  const trialId = route.startsWith("/trials/") ? route.slice("/trials/".length) : null;
  const tab = (path: string, label: string) => (
    <button className={route === path || route.startsWith(`${path}/`) ? "tab on" : "tab"} onClick={() => go(path)}>
      {label}
    </button>
  );
  const page = () => {
    if (caseId) return <Case id={caseId} />;
    if (memberId) return <Household id={memberId} onTask={task} />;
    if (taskId) return <TaskPage id={taskId} onMember={member} />;
    if (trialId) return <TrialPage id={trialId} onBack={() => go("/trials")} />;
    switch (route) {
      case "/cases":
        return <Cases onOpen={open} />;
      case "/members":
        return <Members onOpen={member} />;
      case "/tasks":
        return <Tasks onOpen={task} />;
      case "/dashboard":
        return <Dashboard onOpen={member} />;
      case "/payments":
        return <Payments admin={admin} onMember={member} />;
      case "/trials":
        return <Trials admin={admin} onOpen={(id) => go(`/trials/${id}`)} />;
      case "/tracker":
        return <Tracker />;
      case "/settings":
        return admin ? <Settings /> : <Queue onOpen={open} />;
      default:
        return <Queue onOpen={open} />;
    }
  };
  return (
    <div className="console">
      <header className="top">
        <strong>{me.tenant.name}</strong>
        <nav>
          <button className={route === "/" ? "tab on" : "tab"} onClick={() => go("/")}>
            Queue {waiting > 0 && <span className="badge">{waiting}</span>}
          </button>
          <button className={route.startsWith("/cases") ? "tab on" : "tab"} onClick={() => go("/cases")}>
            Cases {inbox && inbox.pending > 0 && <span className="badge muted-badge">{inbox.pending} to allow</span>}
          </button>
          {tab("/members", "Members")}
          {tab("/tasks", "Tasks")}
          {tab("/dashboard", "Dashboard")}
          {tab("/payments", "Payments")}
          {tab("/trials", "Trials")}
          {tab("/tracker", "Internal AIs")}
          {admin && (
            <button className={route === "/settings" ? "tab on" : "tab"} onClick={() => go("/settings")}>
              Settings
            </button>
          )}
        </nav>
        <span className="muted who">{me.user.email}</span>
        <button className="secondary" onClick={onSignOut}>
          Sign out
        </button>
      </header>
      {inbox?.open_emergencies.map((e) => (
        <div key={e.id} className="banner">
          Emergency from {e.display_name || "a member"} ({e.trigger}). Fixed reply: {e.reply_status}. Bridge:{" "}
          {e.bridge.call ? `rang ${e.bridge.to}` : e.bridge.skipped || e.bridge.error || "starting"}.{" "}
          {e.case_id && <button onClick={() => open(e.case_id!)}>Open the case</button>}
        </div>
      ))}
      <main className="page">{page()}</main>
    </div>
  );
}

export function App() {
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    (async () => {
      try {
        if (!(await refresh())) {
          setState({ kind: "signed-out" });
          return;
        }
        setState({ kind: "signed-in", me: await api<Me>("/api/me") });
      } catch (error) {
        setState({ kind: "error", message: String(error) });
      }
    })();
  }, []);

  if (state.kind === "loading") {
    return <main className="card">Loading…</main>;
  }
  if (state.kind === "error") {
    return (
      <main className="card">
        <p>Something went wrong: {state.message}</p>
        <button onClick={() => window.location.reload()}>Try again</button>
      </main>
    );
  }
  if (state.kind === "signed-out") {
    return (
      <main className="card">
        <h1>Console</h1>
        <p>Sign in with your work Google account.</p>
        <button onClick={() => signIn("/")}>Sign in with Google</button>
      </main>
    );
  }
  const { me } = state;
  const out = async () => {
    await signOut();
    setState({ kind: "signed-out" });
  };
  if (me.roles.some((r) => OPS.includes(r))) {
    return <Console me={me} onSignOut={out} />;
  }
  return (
    <main className="card">
      <h1>{me.tenant.name}</h1>
      <p>
        Signed in as <strong>{me.user.email}</strong>
      </p>
      <p>Roles: {me.roles.map((r) => ROLE_NAMES[r] ?? r).join(", ") || "none"}</p>
      <p>
        Households:{" "}
        {me.households === "all" ? "all of this tenant's" : me.households === "list" ? `${me.household_ids.length} of your own` : "none"}
      </p>
      <button onClick={out}>Sign out</button>
    </main>
  );
}
